import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useTheme } from "@/components/theme-provider";
import { useDensity } from "@/hooks/useDensity";
import { logout } from "@/lib/auth";
import {
  LayoutDashboard,
  ShieldAlert,
  Search,
  Server,
  BookOpen,
  Grid3x3,
  Target,
  Code2,
  Globe,
  Database,
  Lock,
  ShieldCheck,
  Plug,
  ScrollText,
  Settings,
  Bot,
  CheckSquare,
  FileSignature,
  Activity,
  Network,
  DollarSign,
  Send,
  Building2,
  Moon,
  Sun,
  Rows3,
  Rows4,
  LogOut,
  type LucideIcon,
} from "lucide-react";

interface Cmd {
  id: string;
  label: string;
  icon: LucideIcon;
  shortcut?: string;
  run: () => void;
  keywords?: string;
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { resolvedTheme, setTheme } = useTheme();
  const { density, toggle: toggleDensity } = useDensity();

  const go = (path: string) => () => {
    onOpenChange(false);
    navigate(path);
  };

  const overview: Cmd[] = [
    { id: "go-dashboard", label: "Go to Overview", icon: LayoutDashboard, run: go("/dashboard"), keywords: "dashboard home" },
  ];

  const security: Cmd[] = [
    { id: "go-alerts", label: "Alerts", icon: ShieldAlert, run: go("/security/alerts"), keywords: "edr security" },
    { id: "go-events", label: "Events", icon: Search, run: go("/security/events") },
    { id: "go-devices", label: "Devices", icon: Server, run: go("/security/devices") },
    { id: "go-detections", label: "Detections", icon: BookOpen, run: go("/security/detections") },
    { id: "go-mitre", label: "MITRE Coverage", icon: Grid3x3, run: go("/security/mitre") },
    { id: "go-hunts", label: "Hunts", icon: Target, run: go("/security/hunts") },
    { id: "go-sigma", label: "Sigma Rules", icon: Code2, run: go("/security/sigma") },
    { id: "go-ti", label: "Threat Intel", icon: Globe, run: go("/security/threat-intel") },
    { id: "go-persistence", label: "Persistence", icon: Database, run: go("/security/persistence") },
    { id: "go-quarantine", label: "Quarantine", icon: Lock, run: go("/security/quarantine") },
    { id: "go-aiguard", label: "AI Guard", icon: ShieldCheck, run: go("/security/ai-guard") },
    { id: "go-mcp-disc", label: "MCP Discovery", icon: Plug, run: go("/security/mcp-discovery") },
    { id: "go-external", label: "External Sources", icon: Globe, run: go("/security/external-sources") },
    { id: "go-audit", label: "Audit Log", icon: ScrollText, run: go("/security/audit") },
    { id: "go-settings", label: "EDR Settings", icon: Settings, run: go("/security/settings") },
    { id: "ti-refresh", label: "Refresh threat intel", icon: Globe, run: go("/security/external-sources"), keywords: "feed update" },
  ];

  const lighthouse: Cmd[] = [
    { id: "go-agents", label: "Lighthouse Agents", icon: Bot, run: go("/lighthouse/agents"), keywords: "ai governance" },
    { id: "go-approvals", label: "Approval Queue", icon: CheckSquare, run: go("/lighthouse/approvals") },
    { id: "go-policies", label: "Policies", icon: ScrollText, run: go("/lighthouse/policies") },
    { id: "go-pubs", label: "Publishers", icon: Building2, run: go("/lighthouse/publishers") },
    { id: "go-attestations", label: "Attestations", icon: FileSignature, run: go("/lighthouse/attestations") },
    { id: "go-risk", label: "Risk", icon: Activity, run: go("/lighthouse/risk") },
    { id: "go-identities", label: "Identity Bindings", icon: Network, run: go("/lighthouse/identities") },
    { id: "go-cost", label: "Cost Attribution", icon: DollarSign, run: go("/lighthouse/cost") },
    { id: "go-egress", label: "Data Egress", icon: Send, run: go("/lighthouse/egress") },
    { id: "go-connectors", label: "Connectors", icon: Plug, run: go("/lighthouse/connectors") },
    { id: "go-mcp-servers", label: "MCP Servers", icon: Plug, run: go("/lighthouse/mcp-servers") },
    { id: "approve-action", label: "Approve pending action", icon: CheckSquare, run: go("/lighthouse/approvals"), keywords: "approval queue" },
  ];

  const actions: Cmd[] = [
    {
      id: "theme-toggle",
      label: resolvedTheme === "dark" ? "Switch to light mode" : "Switch to dark mode",
      icon: resolvedTheme === "dark" ? Sun : Moon,
      run: () => {
        setTheme(resolvedTheme === "dark" ? "light" : "dark");
        onOpenChange(false);
      },
    },
    {
      id: "density-toggle",
      label: density === "compact" ? "Use comfortable density" : "Use compact density",
      icon: density === "compact" ? Rows3 : Rows4,
      run: () => {
        toggleDensity();
        onOpenChange(false);
      },
    },
    {
      id: "logout",
      label: "Sign out",
      icon: LogOut,
      run: () => {
        onOpenChange(false);
        logout();
      },
    },
  ];

  const renderGroup = (heading: string, items: Cmd[]) => (
    <CommandGroup heading={heading}>
      {items.map((c) => (
        <CommandItem key={c.id} value={`${c.label} ${c.keywords ?? ""}`} onSelect={c.run}>
          <c.icon className="mr-2 h-4 w-4" />
          {c.label}
        </CommandItem>
      ))}
    </CommandGroup>
  );

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search pages, actions…" />
      <CommandList>
        <CommandEmpty>No matches.</CommandEmpty>
        {renderGroup("Overview", overview)}
        <CommandSeparator />
        {renderGroup("Security (EDR)", security)}
        <CommandSeparator />
        {renderGroup("AI Governance (Lighthouse)", lighthouse)}
        <CommandSeparator />
        {renderGroup("Actions", actions)}
      </CommandList>
    </CommandDialog>
  );
}

/** Mounts the palette and binds Cmd/Ctrl+K globally. */
export function CommandPaletteRoot() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return <CommandPalette open={open} onOpenChange={setOpen} />;
}
